// Helpers for "start a material from an HSN code": map the code to the org's category and
// turn the official (very long, upper-case, hierarchical) HSN description into a readable name.

const SMALL_WORDS = new Set(['a', 'an', 'and', 'or', 'of', 'in', 'on', 'the', 'for', 'to', 'with', 'from', 'by', 'at']);

/**
 * Finds the category seeded for the HSN code's 2-digit chapter (category descriptions read
 * "HS Chapter NN: ..."). Prefers a shared type over an org-created one. Returns null when there
 * is no type for that chapter (or the code isn't an HS code, e.g. SAC 99xx service codes).
 */
export function findItemTypeForHsn(code, itemTypes) {
  const digits = String(code || '').replace(/\D/g, '');
  if (digits.length < 2) return null;
  const chapter = parseInt(digits.slice(0, 2), 10);
  const matches = (itemTypes || []).filter((t) => {
    const m = /Chapter\s+(\d{1,2})\b/i.exec(t.description || '');
    return m && parseInt(m[1], 10) === chapter;
  });
  return matches.find((t) => !t.organization) || matches[0] || null;
}

function titleCase(text) {
  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (index > 0 && SMALL_WORDS.has(word)) return word;
      return word.replace(/^([("]*)([a-z])/, (_, lead, ch) => lead + ch.toUpperCase());
    })
    .join(' ');
}

/**
 * Best-effort readable name from an HSN description: the first segment when it's short enough,
 * otherwise the most specific (last) one. It's only a suggestion the user can edit.
 */
export function suggestItemName(description) {
  if (!description) return '';
  const segments = description
    .split(/\s+-\s+|:/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 0) return '';

  const generic = /^(other|others)\b/i;
  let chosen = segments[0];
  if (chosen.length > 60) {
    const last = segments[segments.length - 1];
    chosen = generic.test(last) && segments.length > 1 ? segments[segments.length - 2] : last;
  }

  const words = chosen.split(/\s+/);
  const deduped = words.filter((w, i) => i === 0 || w.toLowerCase() !== words[i - 1].toLowerCase());
  let name = titleCase(deduped.join(' '));
  if (name.length > 80) {
    name = name.slice(0, 80).replace(/\s+\S*$/, '') + '…';
  }
  return name;
}
